import React, {useEffect, useState} from 'react';
import {Button, Checkbox, Form, Input, Modal, Segment} from "semantic-ui-react";
import Info from "../../../../components/Info";
import DragAndDrop from "../../../../components/DragAndDrop";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";

const getDefaultReportName = (options) => {
    const existingNames = new Set((options || []).map(o => o.text));
    let x = 1;
    while (existingNames.has(`Отчет ${x}`)) {
        x++;
    }
    return `Отчет ${x}`;
};

const getStandardReport = (options) => {
    return (options || []).find(o => {
        const data = o.data;
        return data && (data.isUserView === false || data.isUserView === 0 || data.isReadOnly);
    });
};

const getStandardReportColumns = (options) => {
    const option = getStandardReport(options);
    const columns = option && option.data && option.data.columns;
    return columns ? [...columns] : [];
};

const getColumnName = (columnEntry) => columnEntry && (columnEntry.name || columnEntry.Name);

const prepareFormColumns = (sourceColumns, availableColumns) => {
    const cols = [...(sourceColumns || [])];
    const columnNames = cols.map(getColumnName);

    const insertAfter = (anchorName, extName) => {
        if (!columnNames.includes(anchorName) || columnNames.includes(extName)) {
            return;
        }
        const anchorIndex = cols.findIndex(c => getColumnName(c) === anchorName);
        if (anchorIndex < 0 || !availableColumns.find(col => col.id === extName)) {
            return;
        }
        cols.splice(anchorIndex + 1, 0, {
            name: extName,
            order: anchorIndex + 2
        });
        for (let j = anchorIndex + 2; j < cols.length; j++) {
            cols[j].order = j + 1;
        }
        columnNames.splice(anchorIndex + 1, 0, extName);
    };

    insertAfter('senderContactInfo', 'senderExt1');
    insertAfter('recipientContactInfo', 'recipientExt1');
    insertAfter('senderAdditionalPhone', 'senderExt2');
    insertAfter('recipientAdditionalPhone', 'recipientExt2');

    return cols;
};

const RepresentationModal = ({open, onClose, representation, columns, options, onSave, onDelete, loading}) => {
    const [form, setForm] = useState({})
    const [delOpen, setDelOpen] = useState(false)

    const onChange = (e, {value, checked, name}) => {
        setForm(f => ({
            ...f,
            [name]: checked || value
        }))
    }

    useEffect(() => {
        if (!open) {
            setForm({});
            return;
        }

        const standardColumns = getStandardReportColumns(options);

        if (representation) {
            setForm({
                ...representation,
                columns: columns.length
                    ? prepareFormColumns(representation.columns, columns)
                    : [...(representation.columns || [])]
            });
        } else {
            setForm({
                name: getDefaultReportName(options),
                columns: columns.length
                    ? prepareFormColumns(standardColumns, columns)
                    : standardColumns,
                isDefault: false
            });
        }
    }, [open, representation, columns, options])

    const getItems = (isRight) => {
        const right = (form.columns || []).map(c => {
            const columnId = getColumnName(c);
            const col = columns.find(column => column.id === columnId);
            return {
                id: columnId,
                displayNameKey: col && col.displayNameKey
            };
        }).filter(item => item.id && item.displayNameKey);
        
        if (isRight) return right;
        // Фильтруем поля Ext1, Ext2 и старые поля "Добавочный телефон" из левого списка (неиспользуемые данные), но они могут быть добавлены автоматически
        return columns.filter(col => {
            const isHiddenField = col.id === 'senderExt1' || col.id === 'senderExt2' || 
                                  col.id === 'recipientExt1' || col.id === 'recipientExt2' ||
                                  col.id === 'senderAdditional' || col.id === 'recipientAdditional';
            return !isHiddenField && !(right.map(c => c.id).includes(col.id)) && col.displayNameKey;
        });
    }

    const onChangeDnd = (d) => {
        const previousColumns = (form.columns || []).map(getColumnName);
        const newColumns = d.map(cl => cl.id);
        
        let cols = [];
        d.forEach((cl, i) => {
            // Проверяем, что колонка существует в списке доступных колонок
            const colExists = columns.find(col => col.id === cl.id);
            if (!colExists) {
                return; // Пропускаем несуществующие колонки
            }
            
            const colData = (form.columns || []).find(c => getColumnName(c) === cl.id);
            cols.push({
                ...colData,
                name: cl.id,
                order: i + 1
            });
        });

        // Автоматически добавляем Ext1 при добавлении телефона
        // Проверяем, был ли добавлен телефон отправителя
        if (!previousColumns.includes('senderContactInfo') && newColumns.includes('senderContactInfo')) {
            // Добавляем senderExt1 после senderContactInfo, если его еще нет
            const senderPhoneIndex = cols.findIndex(c => c.name === 'senderContactInfo');
            const ext1Exists = cols.some(c => c.name === 'senderExt1');
            if (!ext1Exists && senderPhoneIndex >= 0) {
                // Проверяем, что поле senderExt1 существует в списке доступных колонок
                const ext1Column = columns.find(col => col.id === 'senderExt1');
                if (ext1Column) {
                    cols.splice(senderPhoneIndex + 1, 0, {
                        name: 'senderExt1',
                        order: senderPhoneIndex + 2
                    });
                    // Пересчитываем порядок для всех последующих колонок
                    for (let j = senderPhoneIndex + 2; j < cols.length; j++) {
                        cols[j].order = j + 1;
                    }
                }
            }
        }
        
        // Проверяем, был ли добавлен телефон получателя
        if (!previousColumns.includes('recipientContactInfo') && newColumns.includes('recipientContactInfo')) {
            // Добавляем recipientExt1 после recipientContactInfo, если его еще нет
            const recipientPhoneIndex = cols.findIndex(c => c.name === 'recipientContactInfo');
            const ext1Exists = cols.some(c => c.name === 'recipientExt1');
            if (!ext1Exists && recipientPhoneIndex >= 0) {
                // Проверяем, что поле recipientExt1 существует в списке доступных колонок
                const ext1Column = columns.find(col => col.id === 'recipientExt1');
                if (ext1Column) {
                    cols.splice(recipientPhoneIndex + 1, 0, {
                        name: 'recipientExt1',
                        order: recipientPhoneIndex + 2
                    });
                    // Пересчитываем порядок для всех последующих колонок
                    for (let j = recipientPhoneIndex + 2; j < cols.length; j++) {
                        cols[j].order = j + 1;
                    }
                }
            }
        }
        
        // Автоматически добавляем Ext2 при добавлении дополнительного телефона отправителя
        if (!previousColumns.includes('senderAdditionalPhone') && newColumns.includes('senderAdditionalPhone')) {
            const senderAdditionalPhoneIndex = cols.findIndex(c => c.name === 'senderAdditionalPhone');
            const ext2Exists = cols.some(c => c.name === 'senderExt2');
            if (!ext2Exists && senderAdditionalPhoneIndex >= 0) {
                const ext2Column = columns.find(col => col.id === 'senderExt2');
                if (ext2Column) {
                    cols.splice(senderAdditionalPhoneIndex + 1, 0, {
                        name: 'senderExt2',
                        order: senderAdditionalPhoneIndex + 2
                    });
                    // Пересчитываем порядок для всех последующих колонок
                    for (let j = senderAdditionalPhoneIndex + 2; j < cols.length; j++) {
                        cols[j].order = j + 1;
                    }
                }
            }
        }
        
        // Автоматически добавляем Ext2 при добавлении дополнительного телефона получателя
        if (!previousColumns.includes('recipientAdditionalPhone') && newColumns.includes('recipientAdditionalPhone')) {
            const recipientAdditionalPhoneIndex = cols.findIndex(c => c.name === 'recipientAdditionalPhone');
            const ext2Exists = cols.some(c => c.name === 'recipientExt2');
            if (!ext2Exists && recipientAdditionalPhoneIndex >= 0) {
                const ext2Column = columns.find(col => col.id === 'recipientExt2');
                if (ext2Column) {
                    cols.splice(recipientAdditionalPhoneIndex + 1, 0, {
                        name: 'recipientExt2',
                        order: recipientAdditionalPhoneIndex + 2
                    });
                    // Пересчитываем порядок для всех последующих колонок
                    for (let j = recipientAdditionalPhoneIndex + 2; j < cols.length; j++) {
                        cols[j].order = j + 1;
                    }
                }
            }
        }
        
        // Если телефон удален, удаляем и Ext1
        if (previousColumns.includes('senderContactInfo') && !newColumns.includes('senderContactInfo')) {
            cols = cols.filter(c => c.name !== 'senderExt1');
            // Пересчитываем порядок
            cols.forEach((col, index) => {
                col.order = index + 1;
            });
        }
        
        if (previousColumns.includes('recipientContactInfo') && !newColumns.includes('recipientContactInfo')) {
            cols = cols.filter(c => c.name !== 'recipientExt1');
            // Пересчитываем порядок
            cols.forEach((col, index) => {
                col.order = index + 1;
            });
        }
        
        // Если дополнительный телефон удален, удаляем и Ext2
        if (previousColumns.includes('senderAdditionalPhone') && !newColumns.includes('senderAdditionalPhone')) {
            cols = cols.filter(c => c.name !== 'senderExt2');
            // Пересчитываем порядок
            cols.forEach((col, index) => {
                col.order = index + 1;
            });
        }
        
        if (previousColumns.includes('recipientAdditionalPhone') && !newColumns.includes('recipientAdditionalPhone')) {
            cols = cols.filter(c => c.name !== 'recipientExt2');
            // Пересчитываем порядок
            cols.forEach((col, index) => {
                col.order = index + 1;
            });
        }
        
        onChange(null, {name: 'columns', value: cols})
    }

    const dndKey = representation
        ? `edit-${representation.id}`
        : `create-${(form.columns || []).map(getColumnName).join(',')}`;

    return (
        <Modal
            closeIcon
            onClose={onClose}
            open={open}
            size="small"
            id="fieldModal"
        >
            <Modal.Header className='modal__label_center'>
                {representation ? `Настройка данных отчета` : 'Создать новый отчет'}
            </Modal.Header>
            <Modal.Content className='modal__content representation-content'>
                <div className='representation-content__name-block'>
                    <Form>
                        <Form.Field inline>
                            <label>Название</label>
                            <Input
                                value={form.name || ''}
                                name='name'
                                onChange={onChange}
                            />
                        </Form.Field>
                        {((representation || {}).isUserView || !representation) && <Form.Field className='result-step__item'>
                            <Checkbox
                                checked={form.isDefault || false}
                                name='isDefault'
                                onChange={onChange}
                            />
                            <div className='checkbox-label'>
                                Сделать основным
                                <Info
                                    name={'Основной отчет'}
                                    text={'Данный отчет будет открываться сразу при переходе в раздел “Мои отчеты”'}
                                />
                            </div>

                        </Form.Field>}
                    </Form>
                </div>
                <Segment vertical>
                    <div className='representation-content__dnd scroll-style'>
                        {columns.length > 0 && (
                            <DragAndDrop
                                key={dndKey}
                                left={getItems()}
                                right={getItems(true)}
                            search={''}
                            t={(w) => w + ''}
                            onChange={onChangeDnd}
                            columns={[
                                {
                                    title: 'Неиспользуемые данные'
                                },
                                {
                                    title: 'Данные вашего отчета'
                                }
                            ]}
                            />
                        )}
                    </div>
                </Segment>
            </Modal.Content>
            <Modal.Actions className='modal__actions modal__actions_with-description'>
                <div>
                    <Button loading={loading} disabled={loading} className='modal__actions_yes' onClick={() => onSave(form)}>ПОДТВЕРДИТЬ</Button>
                </div>
                <div className='modal-description'>
                    <div className='personal-data'>
                        Проверьте правильность данных и нажмите кнопку “Подтвердить”
                    </div>
                </div>
                {(representation && representation.isUserView) && <div className='m-t-7'>
                    <div className='personal-data'>
                        <a onClick={() => setDelOpen(true)}>Удалить отчет</a>
                    </div>
                </div>}
            </Modal.Actions>
            <ConfirmModal
                open={delOpen}
                onConfirm={() => {
                    onDelete(form.id);
                    onClose();
                }}
                setOpen={setDelOpen}
                label={'Удаление отчета'}
            >
                Вы действительно уверены, что хотите удалить отчет?
            </ConfirmModal>
        </Modal>
    );
};

export default RepresentationModal;


// WEBPACK FOOTER //
// ./App/pages/reports/components/representation/representationModal.jsx